import { Injectable, signal } from '@angular/core';
import { HttpParams, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, delay, finalize } from 'rxjs/operators';
import { UserAuth, UserTokenData } from './user';
import { environment } from '@env/environment';
import { LocalStorageService } from '@core/local-storage/local-storage.service';
import { Router } from '@angular/router';
import { ApiService } from '@core/services/api.service';
import { LoaderService } from '@shared/components/loader/loader.service';
import { AuthRequest, SenhaRequest } from '@core/models/auth-request';
import { concatMap, tap } from 'rxjs/operators';
import * as dayjs from 'dayjs';
import { MessageService } from '@core/services/message.service';
import { Version } from '@core/models/version';
import { UsuarioMeusDados } from '@core/models/usuario';
import { DomainCacheService } from '@core/services/domain-cache.service';

const routes = {
  getUserData: `prefs`,
  revokeToken: `tokens/revoke`,
  recuperarSenha: (usuario: string) => `${usuario}/recuperarSenha`,
  changeSenha: `usuarios/novaSenha`,
  prolongateToken: `tokens/refresh-token`,
  getVersions: 'publica/version'
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private currentUserSubject: BehaviorSubject<any>;
  public currentUser: Observable<UserAuth>;
  private currentDataExpiracaoSubject: BehaviorSubject<any>;
  public currentDataExpiracao: Observable<Date>;
  private currentTempoSessaoSubject: BehaviorSubject<number>;
  public currentTempoSessao: Observable<number>;
  private loggedIn = new BehaviorSubject<boolean>(false);

  constructor(private apiService: ApiService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private loaderService: LoaderService,
    private messageService: MessageService,
    private domainCacheService: DomainCacheService) {
    this.currentUserSubject = new BehaviorSubject<UserTokenData>(JSON.parse(localStorageService.getItem('currentUser')));
    this.currentUser = this.currentUserSubject.asObservable();
    this.currentDataExpiracaoSubject = new BehaviorSubject<Date>(JSON.parse(localStorageService.getItem('expiration_token')));
    this.currentDataExpiracao = this.currentDataExpiracaoSubject.asObservable();
    this.currentTempoSessaoSubject = new BehaviorSubject<number>(localStorageService.getItem('session_time'));
    this.currentTempoSessao = this.currentTempoSessaoSubject.asObservable();
    this.loggedIn = new BehaviorSubject<boolean>(localStorageService.getItem('token') != undefined);
  }

  public get isLoggedIn() {
    return this.loggedIn.asObservable().pipe(delay(0));
  }

  public get currentUserValue(): UsuarioMeusDados {
    return new UsuarioMeusDados(this.currentUserSubject.value);
  }

  public get dataExpiracaoAcesso(): Date {
    return this.currentDataExpiracaoSubject.value;
  }

  public get tempoSessao(): number {
    return this.currentTempoSessaoSubject.value;
  }

  login(user: AuthRequest) {

    return this.apiService.postCustomApi<any>(`${environment.auth}/tokens`, user)

      .pipe(tap(res => {
        return res;
      }),
        concatMap((res) => {
          if (!res.autenticaEmDuasEtapas && res.token) {
            return this.getUserData(res.token).pipe(map(data => {
              if (!res.senhaExpirado)
                this.salvarToken(res);


              return { ...data, ...res };
            }));
          }
          return of(res);
        }),
        map((data: UsuarioMeusDados) => {
          return data;
        }));
  }

  prolongateToken(token?: string): Observable<any> {
    if (!token) {
      token = this.localStorageService.getItem('token');
      token = token!.replace(/^"(.*)"$/, '$1');
    }
    const body = { token }; // Corpo da solicitação com a propriedade refreshToken
    return this.apiService.postCustomApi<any>(`${environment.auth}/${routes.prolongateToken}`, body).pipe(
      tap(res => {
        if (res.token) {
          this.salvarToken(res);
        }
      })
    );
  }


  salvarToken(res: any) {
    const now = new Date();
    const expire = dayjs(now).add(res.tempoDeExpiracao, 'minute').toDate();
    this.localStorageService.setItem('token', JSON.stringify(res.token));
    this.setExpirationDate(expire);
    this.setTempoSessao(res.tempoDeExpiracao);
    this.currentDataExpiracaoSubject.next(expire);
  }

  recuperarSenha(login: string) {
    return this.apiService.getCustomApi<any>(`${environment.auth}/${routes.recuperarSenha(login)}`);
  }

  logout(setReturnUrl?: boolean, message?: string, acaoUsuario?: boolean) {
    if (!this.router.url.startsWith('/login')) {
      const logoutCall = () => {
        this.clearLocalStorage(acaoUsuario);
        this.currentUserSubject.next(null);
        this.loggedIn.next(false);
        this.loaderService.hide();

        const redirectUrl = '/login';

        this.revokeToken().pipe(
          tap(() => {
            this.router.navigate([redirectUrl])
            this.loaderService.hide();
          })
        ).subscribe({
          error: () => {
            this.router.navigate([redirectUrl])
            this.loaderService.hide();
          }
        });
      };

      logoutCall();
    }
  }

  clearLocalStorage(acaoUsuario: any) {
    this.localStorageService.removeItem('currentUser');
    this.localStorageService.removeItem('token');
    this.localStorageService.removeItem('expiration_token');
    this.localStorageService.removeItem('session_time');

    if (acaoUsuario){
      this.localStorageService.removeItem('device_token');
    }

    this.domainCacheService.clear();
    this.currentUserSubject.next(null);
    this.currentDataExpiracaoSubject.next(null);
    this.loggedIn.next(false);
    this.loaderService.hide();
  }

  getUserData(token?: string) {
    if (token) {
      let header = new HttpHeaders().append('Authorization', token);
      return this.apiService.getCustomApi<any>(`${environment.auth}/${routes.getUserData}`, undefined, header);
    }

    return this.apiService.getCustomApi<any>(`${environment.auth}/${routes.getUserData}`);
  }

  revokeToken() {
    let params = new HttpParams().append('ignoreError', 'true');
    return this.apiService.getCustomApi<any>(`${environment.auth}/${routes.revokeToken}`, params);
  }


  alterarSenha(obj: SenhaRequest, token: string) {
    let header = new HttpHeaders();
    header = header.append('Authorization', token);
    return this.apiService.putCustomApi<any>(`${environment.api}/${routes.changeSenha}`, obj, header);
  }


  setCurrentUser(dadosUsuario: any) {
    this.localStorageService.setItem('currentUser', JSON.stringify(dadosUsuario));
    this.currentUserSubject.next(dadosUsuario);
    this.loggedIn.next(true);
  }

  loginWithToken(token: string) {
    this.localStorageService.setItem('token', JSON.stringify(token));
    this.loggedIn.next(true);
  }

  setExpirationDate(expire: Date) {

    this.localStorageService.setItem('expiration_token', JSON.stringify(expire));
    this.currentDataExpiracaoSubject.next(expire);
  }

  setTempoSessao(tempo: number) {
    this.localStorageService.setItem('session_time', tempo);
    this.currentTempoSessaoSubject.next(tempo);
  }

  getTokenIdentificadorDispositivo(user: AuthRequest) {
    if (this.localStorageService.getItem('device_token') != undefined) {
      return this.localStorageService.getItem('device_token');
    }
    else {
      var AES = require("crypto-js/aes");
      var data = { sub: user.login, data: dayjs().toDate() }
      var encryptData = AES.encrypt(JSON.stringify(data), environment.secretKey).toString();
      this.localStorageService.setItem('device_token', encryptData);
      return encryptData;
    }
  }

  clearTokenIdentificadorDispositivo(): void {
    this.localStorageService.removeItem('device_token');
  }




}
